import { z } from 'zod';
import { configFileCategory, isSegregatedCategory } from '../files/categories.js';
import { cleanProfileFieldValues } from '../people/profile-fields.js';
export const stepStateSchema = z.enum([
    'not_started',
    'sent',
    'in_progress',
    'submitted',
    'approved',
    'rejected',
    'cancelled',
]);
/**
 * One section of a form and how far the person has got with it (ADR-013 §4). HR's "sections
 * done out of total" and the portal's "Saved" checks read the same function, so they cannot
 * disagree.
 */
export const stepSectionStateSchema = z.enum(['done', 'in-progress', 'not-started']);
export const stepSectionSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    state: stepSectionStateSchema,
}).strict();
function toIssues(error) {
    return error.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`);
}
/**
 * Read generically so the segregation guard on `profileFields` covers a type that does not exist
 * yet — which is exactly what reading only `target` stopped it doing. Most types name the category
 * `target` (document-upload, pdf-form, date-credential, acknowledgment, candidate-application);
 * `hr-record` names it `fileCategory`, and a type that named it that way would have had its
 * segregated `profileFields` copied onto the person profile. `configFileCategory` reads both.
 */
function filesToSegregatedCategory(config) {
    const category = configFileCategory(config);
    return category !== null && isSegregatedCategory(category);
}
function run(schema, input) {
    const parsed = schema.safeParse(input);
    return parsed.success ? { ok: true, value: parsed.data } : { ok: false, issues: toIssues(parsed.error) };
}
/**
 * Build a step type. Erasure happens inside this function, where TConfig and
 * TSubmission are still concrete — which is why the whole contract needs no casts.
 */
export function defineStepType(spec) {
    const runtime = {
        validateConfig: (input) => {
            const parsed = run(spec.configSchema, input);
            return parsed.ok ? { ok: true, value: { type: spec.type, config: parsed.value } } : parsed;
        },
        validateSubmission: (input) => {
            const parsed = run(spec.submissionSchema, input);
            return parsed.ok ? { ok: true, value: { type: spec.type, submission: parsed.value } } : parsed;
        },
        approve: (instance) => {
            const config = run(spec.configSchema, instance.config);
            if (!config.ok)
                return config;
            const submission = run(spec.submissionSchema, instance.submission);
            if (!submission.ok)
                return submission;
            const refused = spec.refuseApproval?.({ config: config.value, submission: submission.value }) ?? null;
            if (refused !== null)
                return { ok: false, issues: [refused] };
            return { ok: true, value: spec.onApprove({ instance, config: config.value, submission: submission.value }) };
        },
        readyToSubmit: (configInput, draft) => {
            if (!spec.readyToSubmit)
                return run(spec.submissionSchema, draft).ok;
            const config = run(spec.configSchema, configInput);
            // A config that does not parse cannot be reasoned about, and enabling the button on a
            // misconfigured step would send the person into a refusal they cannot act on.
            return config.ok ? spec.readyToSubmit({ config: config.value, draft }) : false;
        },
        profileFields: (configInput, value) => {
            if (!spec.profileFields)
                return [];
            const config = run(spec.configSchema, configInput);
            if (!config.ok || filesToSegregatedCategory(config.value))
                return [];
            return cleanProfileFieldValues(spec.profileFields({ config: config.value, value }));
        },
        sections: (configInput, value) => {
            if (!spec.sections)
                return [];
            const config = run(spec.configSchema, configInput);
            return config.ok ? spec.sections({ config: config.value, value }) : [];
        },
        signatureOf: (configInput, submissionInput) => {
            if (!spec.signature)
                return null;
            const config = run(spec.configSchema, configInput);
            const submission = run(spec.submissionSchema, submissionInput);
            // A submission that does not parse has not been stored, so there is nothing signed to
            // evidence. Returning null rather than throwing keeps this off the refusal path.
            if (!config.ok || !submission.ok)
                return null;
            return spec.signature({ config: config.value, submission: submission.value });
        },
        uploadsNamedBy: (configInput, submissionInput) => {
            if (!spec.uploads)
                return null;
            const config = run(spec.configSchema, configInput);
            const submission = run(spec.submissionSchema, submissionInput);
            // null, NOT []: a submission that does not parse has told us nothing about which uploads it
            // names, and answering "none" would have the caller drop this step's documents.
            if (!config.ok || !submission.ok)
                return null;
            return [...new Set(spec.uploads({ config: config.value, submission: submission.value })
                    .filter((id) => typeof id === 'string' && id.length > 0))];
        },
        checkSubmission: async (pinnedContent, configInput, submissionInput, now) => {
            if (!spec.checkSubmission)
                return { ok: true };
            const config = run(spec.configSchema, configInput);
            if (!config.ok)
                return { ok: false, message: config.issues.join('; ') };
            const submission = run(spec.submissionSchema, submissionInput);
            if (!submission.ok)
                return { ok: false, message: submission.issues.join('; ') };
            return spec.checkSubmission({ config: config.value, submission: submission.value, pinnedContent, now });
        },
        expiresAt: (instance) => {
            if (!spec.expiresAt)
                return { ok: true, value: null };
            const config = run(spec.configSchema, instance.config);
            if (!config.ok)
                return config;
            const submission = run(spec.submissionSchema, instance.submission);
            if (!submission.ok)
                return submission;
            return { ok: true, value: spec.expiresAt({ config: config.value, submission: submission.value }) };
        },
    };
    return { ...spec, runtime };
}
//# sourceMappingURL=contract.js.map